import { useState } from 'react';
import { useAppContext } from '../context/AppContext';
import { formatTL, calculatePricing, type PricingCalculations } from '../lib/pricing';
import { resolveConfigNames, sortVisualAttachmentItems, buildPreviewPricing, type PreviewRow } from '../lib/proposalDerived';
import { BRAND_LOGO_URL } from '../constants';
import { chunkArray, formatDateTurkish, formatDateNumeric, formatMonthYearTurkish } from '../utils';
import type { GlobalConfig, SpecItem } from '../types';
import { ZoomIn, X, ChevronLeft, ChevronRight, Download, Image as ImageIcon, Lightbulb, Sliders, Palette, Box, Building2 } from 'lucide-react';

function SinkIcon(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.0" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <rect x="2.5" y="4.5" width="19" height="15" rx="1.5" strokeWidth="1.1" />
      <line x1="11.5" y1="4.5" x2="11.5" y2="19.5" strokeWidth="0.9" />
      <line x1="4.5" y1="7.5" x2="9.5" y2="7.5" strokeWidth="0.85" />
      <line x1="4.5" y1="10.5" x2="9.5" y2="10.5" strokeWidth="0.85" />
      <line x1="4.5" y1="13.5" x2="9.5" y2="13.5" strokeWidth="0.85" />
      <line x1="4.5" y1="16.5" x2="9.5" y2="16.5" strokeWidth="0.85" />
      <rect x="13.5" y="6.5" width="6" height="11" rx="1" strokeWidth="0.9" />
      <circle cx="16.5" cy="12" r="1" strokeWidth="0.85" />
    </svg>
  );
}

function CabinetAccessoryIcon(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.0" strokeLinecap="round" strokeLinejoin="round" {...props}>
      {/* Outer tall cabinet carcass (left) */}
      <rect x="2.5" y="3" width="6.5" height="18" rx="0.8" strokeWidth="1.1" />
      
      {/* Front door panel (right) */}
      <rect x="18" y="3.5" width="3.5" height="17" rx="0.5" strokeWidth="1.0" />
      <line x1="19.8" y1="7" x2="19.8" y2="11" strokeWidth="1.1" />
      
      {/* 3 Tiered pull-out shelf baskets & rails */}
      {/* Top shelf */}
      <rect x="9" y="7" width="9" height="1.8" rx="0.3" strokeWidth="0.85" />
      <line x1="9" y1="7.9" x2="18" y2="7.9" strokeWidth="0.8" />
      <rect x="10.3" y="4.5" width="2" height="2.5" rx="0.3" strokeWidth="0.75" />
      <rect x="13.3" y="4.5" width="2" height="2.5" rx="0.3" strokeWidth="0.75" />

      {/* Middle shelf */}
      <rect x="9" y="13" width="9" height="1.8" rx="0.3" strokeWidth="0.85" />
      <line x1="9" y1="13.9" x2="18" y2="13.9" strokeWidth="0.8" />
      <circle cx="11.2" cy="11.2" r="1.1" strokeWidth="0.75" />
      <circle cx="14.5" cy="11.2" r="1.1" strokeWidth="0.75" />

      {/* Bottom shelf */}
      <rect x="9" y="19" width="9" height="1.8" rx="0.3" strokeWidth="0.85" />
      <line x1="9" y1="19.9" x2="18" y2="19.9" strokeWidth="0.8" />
      <path d="M 10.8 19 V 17 C 10.8 16.2 11.3 15.8 11.6 15.8 V 15 H 12 V 15.8 C 12.3 15.8 12.8 16.2 12.8 17 V 19" strokeWidth="0.75" />
      <path d="M 14.3 19 V 17 C 14.3 16.2 14.8 15.8 15.1 15.8 V 15 H 15.5 V 15.8 C 15.8 15.8 16.3 16.2 16.3 17 V 19" strokeWidth="0.75" />
    </svg>
  );
}

function CountertopTextureIcon(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.0" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <rect x="3" y="3" width="18" height="18" rx="1.5" strokeWidth="1.1" />
      <line x1="6" y1="6.5" x2="8" y2="7.5" strokeWidth="0.8" />
      <line x1="12" y1="5.5" x2="14" y2="6.5" strokeWidth="0.8" />
      <line x1="16.5" y1="8" x2="18.5" y2="7" strokeWidth="0.8" />
      <line x1="7" y1="11" x2="9" y2="13" strokeWidth="0.8" />
      <line x1="11.5" y1="10" x2="13.5" y2="9" strokeWidth="0.8" />
      <line x1="15.5" y1="12.5" x2="13.5" y2="14.5" strokeWidth="0.8" />
      <line x1="6.5" y1="16" x2="8.5" y2="17" strokeWidth="0.8" />
      <line x1="11" y1="17.5" x2="13" y2="16" strokeWidth="0.8" />
      <line x1="16" y1="17" x2="17.5" y2="18.5" strokeWidth="0.8" />
    </svg>
  );
}

function OvenApplianceIcon(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.0" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <rect x="3" y="3" width="18" height="18" rx="1.5" strokeWidth="1.1" />
      <line x1="3" y1="8" x2="21" y2="8" strokeWidth="0.9" />
      <circle cx="6.5" cy="5.5" r="0.9" strokeWidth="0.85" />
      <circle cx="10" cy="5.5" r="0.9" strokeWidth="0.85" />
      <circle cx="14" cy="5.5" r="0.9" strokeWidth="0.85" />
      <circle cx="17.5" cy="5.5" r="0.9" strokeWidth="0.85" />
      <rect x="5.5" y="10.5" width="13" height="8.5" rx="1" strokeWidth="0.9" />
      <line x1="8" y1="9.2" x2="16" y2="9.2" strokeWidth="1.0" />
    </svg>
  );
}

function FaucetIcon(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.0" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M 6 20 V 10 C 6 5.5 9.5 3 13.5 3 C 16.5 3 18.5 5 18.5 8 V 10" strokeWidth="1.1" />
      <rect x="17" y="10" width="3" height="1.5" rx="0.5" strokeWidth="0.85" />
      <path d="M 18.5 11.5 C 18.5 13 18.5 14.5 18.5 15 C 18.5 15.5 18 16 17.5 16 C 17 16 16.5 15.5 16.5 15 C 16.5 14 18.5 11.5 18.5 11.5 Z" strokeWidth="0.85" />
      <rect x="4.5" y="19" width="7" height="2" rx="0.5" strokeWidth="0.9" />
      <line x1="8" y1="15" x2="13" y2="15" strokeWidth="1.0" />
      <circle cx="13" cy="15" r="0.8" strokeWidth="0.85" />
    </svg>
  );
}

function ProductImageThumbnail({ item }: { item: { category?: string; name?: string; image?: string | null } }) {
  const [error, setError] = useState(false);

  if (item.image && !error) {
    return (
      <img
        src={item.image}
        alt={item.name || ''}
        className="w-20 h-20 rounded-lg border border-stone-100 bg-white flex-shrink-0 shadow-2xs object-contain p-1"
        onError={() => setError(true)}
      />
    );
  }

  const category = item.category || '';
  const getIcon = () => {
    switch (category) {
      case 'Eviye':
        return <SinkIcon className="w-10 h-10 text-stone-600" />;
      case 'Aksesuar':
        return <CabinetAccessoryIcon className="w-10 h-10 text-stone-600" />;
      case 'Tezgâh':
      case 'Tezgah':
        return <CountertopTextureIcon className="w-10 h-10 text-stone-600" />;
      case 'Cihaz':
        return <OvenApplianceIcon className="w-10 h-10 text-stone-600" />;
      case 'Armatür':
      case 'Armatur':
        return <FaucetIcon className="w-10 h-10 text-stone-600" />;
      case 'Aydınlatma':
      case 'Aydinlatma':
        return <Lightbulb className="w-10 h-10 text-stone-600 stroke-[1]" />;
      case 'Kulp':
        return <Sliders className="w-10 h-10 text-stone-600 stroke-[1]" />;
      case 'Renk':
      case 'Gövde Rengi':
        return <Palette className="w-10 h-10 text-stone-600 stroke-[1]" />;
      default:
        return <Box className="w-10 h-10 text-stone-600 stroke-[1]" />;
    }
  };

  return (
    <div className="w-20 h-20 rounded-xl border border-stone-200 bg-stone-50/60 flex items-center justify-center flex-shrink-0 shadow-2xs">
      {getIcon()}
    </div>
  );
}

interface KitchenPreviewSectionProps {
  sectionLabel?: string; // Daire tipi label, shown as a banner heading in bulk mode
  pageNumberPrefix: string; // "01", "02"... — restarts per daire tipi so each reads as its own mini-proposal
  config: GlobalConfig;
  resolvedModelName: string;
  resolvedMaterialName: string;
  resolvedDoorColor: string;
  resolvedBodyColor: string;
  resolvedGlassType: string;
  resolvedProfileColor: string;
  pricingCalculations: PricingCalculations;
  previewRows: PreviewRow[];
  hasExtraDiscount: boolean;
  cabinetsCampaignSavings: number;
  otherRowsDiscountSavings: number;
  allVisualAttachmentItems: SpecItem[];
  projectImages: string[];
  quantity?: number; // daire adedi — fiyat tablosu tek daire yerine bu adet üzerinden gösterilir (toplu proje)
  showPricing?: boolean; // toplu projede fiyat artık Proje Geneli Özet tablosunda elle girildiği için bu sayfada gösterilmez
  salesPointDiscount?: number; // satış noktasının küsuratı yuvarlamak için girdiği KDV dahil TL tutarı — yalnızca retail teklifte kullanılıyor
}

function KitchenPreviewSection({
  sectionLabel, pageNumberPrefix, config, resolvedModelName, resolvedMaterialName, resolvedDoorColor, resolvedBodyColor, resolvedGlassType, resolvedProfileColor,
  pricingCalculations, previewRows, hasExtraDiscount, cabinetsCampaignSavings, otherRowsDiscountSavings,
  allVisualAttachmentItems, projectImages, quantity = 1, showPricing = true, salesPointDiscount = 0,
}: KitchenPreviewSectionProps) {
  const [previewModalIndex, setPreviewModalIndex] = useState<number | null>(null);

  // Sayfa sırası: kapaktan hemen sonra proje görselleri, ardından fiyat/özet, en son ürün görsel detayları
  const formatPageNum = (n: number) => (n < 10 ? `0${n}` : `${n}`);
  const basePage = Number(pageNumberPrefix);
  let nextPage = basePage;
  const projectImagesPageNum = projectImages.length > 0 ? nextPage++ : null;
  const summaryPageNum = nextPage++;
  const visualAttachmentsPageNum = allVisualAttachmentItems.length > 0 ? nextPage++ : null;

  return (
    <div className="space-y-12">
      {sectionLabel && (
        <div className="page-break-before flex items-center space-x-3 pt-6 pb-2">
          <Building2 className="w-4 h-4 text-brand-600 shrink-0" />
          <h2 className="font-semibold text-stone-800 uppercase tracking-[0.15em] text-[10pt]">{sectionLabel}</h2>
          <div className="flex-1 h-[1px] bg-stone-200" />
        </div>
      )}

      {/* Project images output page — kapaktan hemen sonra gelir */}
      {projectImages.length > 0 && (
        <div className={`${sectionLabel ? '' : 'page-break-before'} pt-6 space-y-6`}>
          <div className="flex justify-between items-center border-b border-stone-200 pb-2">
            <h2 className="font-semibold text-stone-850 uppercase tracking-[0.18em] text-[8pt]">{formatPageNum(projectImagesPageNum!)} / Görsel Tasarım Detayları</h2>
            {BRAND_LOGO_URL && (
              <img src={BRAND_LOGO_URL} alt="İntema Logo" width={110} height={44} className="h-10 md:h-11 print:hidden w-auto object-contain shrink-0" />
            )}
          </div>

          {/* Output images 2-per-page (stacked broad render) */}
          <div className="flex flex-col gap-6">
            {chunkArray(projectImages, 2).map((chunk, groupIndex) => (
              <div key={groupIndex} className="flex flex-col gap-6 page-break-inside-avoid print:pt-16">
                {chunk.map((imgUrl, idx) => {
                  const globalIdx = groupIndex * 2 + idx;
                  return (
                    <div
                      key={idx}
                      onClick={() => setPreviewModalIndex(globalIdx)}
                      className="border border-stone-200 rounded-xl overflow-hidden bg-stone-50/20 p-2 cursor-pointer group relative hover:border-brand-400 transition-all shadow-xs"
                    >
                      <img
                        src={imgUrl}
                        alt={`Tasarım Detayı - Sayfa ${groupIndex + 1} Görsel ${idx + 1}`}
                        className="w-full h-[10.5cm] object-contain rounded-lg"
                      />

                      {/* Hover Overlay */}
                      <div className="absolute inset-2 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-all rounded-lg print:hidden">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setPreviewModalIndex(globalIdx);
                          }}
                          className="bg-white/90 hover:bg-white text-stone-800 px-3 py-1.5 rounded-full text-xs font-bold shadow-md flex items-center space-x-1.5 hover:scale-105 transition-all"
                        >
                          <ZoomIn className="w-4 h-4 text-stone-900" />
                          <span>Büyük Boy İncele</span>
                        </button>
                      </div>

                      <span className="text-[7.5pt] font-semibold text-stone-400 block mt-2 text-center uppercase tracking-wider">
                        TASARIM DETAYI {globalIdx + 1}
                      </span>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Page: Proje Özet ve Fiyat Detayı */}
      <div className={`${sectionLabel && projectImages.length === 0 ? '' : 'page-break-before'} pt-6 space-y-8 print:space-y-4`}>

        <div className="flex justify-between items-center border-b border-stone-200 pb-2">
          <h2 className="font-semibold text-stone-850 uppercase tracking-[0.18em] text-[8pt]">{formatPageNum(summaryPageNum)} / Proje Özeti{showPricing ? ' & Fiyat Detayları' : ''}</h2>
          {BRAND_LOGO_URL && (
            <img src={BRAND_LOGO_URL} alt="İntema Logo" width={110} height={44} className="h-10 md:h-11 print:hidden w-auto object-contain shrink-0" />
          )}
        </div>

        {/* Configuration Summary Table */}
        <div className="space-y-3">
          <div className="flex items-center space-x-4">
            <div className="text-[8pt] text-stone-400 font-sans tracking-[0.18em] font-semibold">01</div>
            <h3 className="font-semibold text-stone-800 uppercase tracking-[0.18em] text-[8pt] whitespace-nowrap">
              Model & Malzeme Spesifikasyonları
            </h3>
          </div>

          <div className="py-4 print:py-1">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-y-6 print:gap-y-3 gap-x-8 font-sans">
              <div className="space-y-1">
                <div className="text-[6.5pt] font-semibold text-stone-400 uppercase tracking-wider">MUTFAK MODELİ</div>
                <div className="text-[8.5pt] font-medium text-stone-900 uppercase">{resolvedModelName || '-'}</div>
              </div>
              <div className="space-y-1">
                <div className="text-[6.5pt] font-semibold text-stone-400 uppercase tracking-wider">KAPAK MALZEMESİ</div>
                <div className="text-[8.5pt] font-medium text-stone-900">{resolvedMaterialName || '-'}</div>
              </div>
              <div className="space-y-1">
                <div className="text-[6.5pt] font-semibold text-stone-400 uppercase tracking-wider">KAPAK RENGİ</div>
                <div className="text-[8.5pt] font-medium text-stone-900">{resolvedDoorColor || '-'}</div>
              </div>
              {resolvedBodyColor && (
                <div className="space-y-1">
                  <div className="text-[6.5pt] font-semibold text-stone-400 uppercase tracking-wider">GÖVDE RENGİ</div>
                  <div className="text-[8.5pt] font-medium text-stone-900">{resolvedBodyColor}</div>
                </div>
              )}

              <div className="space-y-1">
                <div className="text-[6.5pt] font-semibold text-stone-400 uppercase tracking-wider">BAZA YÜKSEKLİĞİ</div>
                <div className="text-[8.5pt] font-medium text-stone-900">{config.plinthHeight || '-'}</div>
              </div>
              {resolvedGlassType && (
                <div className="space-y-1">
                  <div className="text-[6.5pt] font-semibold text-stone-400 uppercase tracking-wider">CAM TİPİ</div>
                  <div className="text-[8.5pt] font-medium text-stone-900">{resolvedGlassType}</div>
                </div>
              )}
              {resolvedProfileColor && (
                <div className="space-y-1">
                  <div className="text-[6.5pt] font-semibold text-stone-400 uppercase tracking-wider">PROFİL RENGİ</div>
                  <div className="text-[8.5pt] font-medium text-stone-900">{resolvedProfileColor}</div>
                </div>
              )}
              {config.profileType && (
                <div className="space-y-1">
                  <div className="text-[6.5pt] font-semibold text-stone-400 uppercase tracking-wider">PROFİL TİPİ</div>
                  <div className="text-[8.5pt] font-medium text-stone-900">{config.profileType}</div>
                </div>
              )}
              <div className="space-y-1">
                <div className="text-[6.5pt] font-semibold text-stone-400 uppercase tracking-wider">TERMİN SÜRESİ</div>
                <div className="text-[8.5pt] font-medium text-stone-900">{config.leadTime || '-'}</div>
              </div>
            </div>
          </div>
        </div>

        {showPricing && (
          <>
            {/* Quoting Pricing Summary Table (No unit prices, no quantities) */}
            <div className="space-y-3">
              <div className="flex items-center space-x-4">
                <div className="text-[8pt] text-stone-400 font-sans tracking-[0.18em] font-semibold">02</div>
                <h3 className="font-semibold text-stone-800 uppercase tracking-[0.18em] text-[8pt] whitespace-nowrap">
                  Fiyat Detay Özeti
                </h3>
              </div>
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="border-b border-stone-300 text-stone-500 uppercase text-[6.5pt] font-semibold tracking-wider">
                    <th className="py-2.5">Hizmet / Ürün Grubu</th>
                    <th className="py-2.5 pr-4 text-right w-32">Tavsiye Liste Fiyatı</th>
                    <th className="py-2.5 px-4 text-center w-28">Kampanya İndirimi</th>
                    {hasExtraDiscount && <th className="py-2.5 px-4 text-center w-24">Ek İndirim</th>}
                    <th className="py-2.5 px-4 text-center w-20">KDV Oranı</th>
                    <th className="py-2.5 pl-4 text-right w-32">KDV Dahil Tutar</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-100 text-[8.5pt]">
                  {previewRows.map(row => (
                    <tr key={row.key} className="page-break-inside-avoid">
                      <td className="py-3 print:py-1.5 font-normal text-stone-800">{row.displayName}</td>
                      <td className="py-3 print:py-1.5 pr-4 text-right">{row.listPrice === 0 ? '-' : formatTL(row.listPrice * quantity)}</td>
                      <td className="py-3 print:py-1.5 px-4 text-center text-stone-600">
                        {typeof row.campaignDiscount === 'number' && row.campaignDiscount > 0
                          ? `%${parseFloat((row.campaignDiscount * 100).toFixed(4)).toString().replace('.', ',')}`
                          : '-'}
                      </td>
                      {hasExtraDiscount && (
                        <td className="py-3 print:py-1.5 px-4 text-center text-stone-600">
                          {typeof row.extraDiscount === 'number' && row.extraDiscount > 0
                            ? `%${Math.round(row.extraDiscount * 100)}`
                            : '-'}
                        </td>
                      )}
                      <td className="py-3 print:py-1.5 px-4 text-center text-stone-600">
                        {row.vatRate > 0 ? `%${Math.round(row.vatRate * 100)}` : '-'}
                      </td>
                      <td className="py-3 print:py-1.5 pl-4 text-right font-semibold text-stone-800">{formatTL(row.incVatPrice * quantity)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Calculations summaries */}
            <div className="flex justify-end page-break-inside-avoid">
              <div className="w-80 space-y-2 pt-3 text-[8.5pt] font-sans">
                <div className="flex justify-between text-stone-500">
                  <span>Tavsiye Liste Fiyatı Toplamı:</span>
                  <span>{formatTL(pricingCalculations.listGrandTotal * quantity)}</span>
                </div>
                {cabinetsCampaignSavings > 0 && (
                  <div className="flex justify-between text-emerald-600 font-medium">
                    <span>Kampanya İndirimi:</span>
                    <span>-{formatTL(cabinetsCampaignSavings * quantity)}</span>
                  </div>
                )}
                {otherRowsDiscountSavings > 0 && (
                  <div className="flex justify-between text-emerald-600 font-medium">
                    <span>Diğer İndirim:</span>
                    <span>-{formatTL(otherRowsDiscountSavings * quantity)}</span>
                  </div>
                )}
                <div className="flex justify-between text-stone-500">
                  <span>KDV Hariç Net Toplam:</span>
                  <span>{formatTL(pricingCalculations.netGrandTotal * quantity)}</span>
                </div>
                <div className="flex justify-between text-stone-500">
                  <span>KDV Toplamı:</span>
                  <span>{formatTL(pricingCalculations.totalVat * quantity)}</span>
                </div>
                {salesPointDiscount > 0 && (
                  <div className="flex justify-between text-emerald-600 font-medium">
                    <span>Satış Noktası Özel İskonto:</span>
                    <span>-{formatTL(salesPointDiscount)}</span>
                  </div>
                )}
                <div className="flex justify-between items-center border-t-2 border-stone-800 !mt-4 pt-4 text-stone-900">
                  <span className="font-bold uppercase tracking-wider text-[8pt]">Genel Toplam (KDV Dahil):</span>
                  <span className="text-[16pt] font-bold text-stone-900">{formatTL(Math.max(0, pricingCalculations.finalTotal * quantity - salesPointDiscount))}</span>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Terms and preconditions block */}
        <div className="pt-6 print:pt-3 border-t border-stone-250 text-[7.5pt] text-stone-500 leading-relaxed page-break-inside-avoid space-y-2 print:space-y-1">
          <p>• Planlanan teslim tarihi, sipariş onayını takip eden resmi tatiller dışında kalan iş günlerini kapsar ve seçilen kapak grubuna göre değişiklik gösterir.</p>
          <p>• Proje özetinde adı geçmeyen herhangi bir aksesuar veya ürün teklife dahil değildir.</p>
          <p>• Teklif geçerlilik süresi yalnızca teklif formunda belirtilen tarih için geçerlidir.</p>
          <p>• Termin süresi seçilen kapak grubuna göre değişiklik gösterir. Teklife konu olan projenin üretim termin süresi <strong className="font-bold text-stone-700">{config.leadTime ? config.leadTime.replace(' hafta', '') : '……'}</strong> haftadır. Bu tarihten itibaren proje danışmanı, müşterinin müsaitliğini de göz önünde bulundurarak üretim bitiş tarihinden itibaren 7-10 iş günü içerisinde nakliye ve montaj sürecini organize edecektir.</p>
        </div>

        {/* Proje Notu — yalnızca kullanıcı bir not girdiyse basılır; sayfa yapısını bozmamak için kısa ve tek satırda kırpılabilir tutulur */}
        {config.projectNote && config.projectNote.trim() && (
          <div className="pt-4 border-t border-stone-250 page-break-inside-avoid">
            <div className="text-[6.5pt] font-semibold text-stone-400 uppercase tracking-wider mb-1.5">Proje Notu</div>
            <p className="text-[7.5pt] text-stone-600 leading-relaxed line-clamp-4 whitespace-pre-line">
              {config.projectNote.trim()}
            </p>
          </div>
        )}

      </div>

      {/* Visual attachments card deck */}
      {allVisualAttachmentItems.length > 0 && (
        <div className="space-y-4 page-break-before pt-6">
          <div className="flex justify-between items-center border-b border-stone-200 pb-2">
            <h2 className="font-semibold text-stone-850 uppercase tracking-[0.18em] text-[8pt]">{formatPageNum(visualAttachmentsPageNum!)} / Teklife Konu Görsel Ürün Detayları</h2>
            {BRAND_LOGO_URL && (
              <img src={BRAND_LOGO_URL} alt="İntema Logo" width={110} height={44} className="h-10 md:h-11 print:hidden w-auto object-contain shrink-0" />
            )}
          </div>
          <div className="grid grid-cols-2 gap-4">
            {allVisualAttachmentItems.map(item => (
              <div key={item.id} className="border border-stone-150 p-4.5 rounded-xl flex items-center space-x-4 bg-stone-50/20 page-break-inside-avoid shadow-xs">
                <ProductImageThumbnail item={item} />
                <div className="space-y-1.5 overflow-hidden flex-1">
                  <div className="font-semibold text-stone-900 text-[10px] tracking-tight truncate">{item.code}</div>
                  <div className="text-[10px] text-stone-500 font-medium truncate leading-normal">{item.name.replace('Lütfen yerleşim bilgisi giriniz', '')}</div>
                  <div className="text-[8px] text-brand-700 font-semibold uppercase tracking-wider">{item.category === 'Renk' ? 'Kapak Rengi' : item.category}</div>
                </div>
                {!!item.quantity && (
                  <div className="shrink-0 text-center pr-2">
                    <div className="text-[7px] text-stone-400 font-semibold uppercase tracking-wider">Adet</div>
                    <div className="text-[10px] font-semibold text-stone-900 tabular-nums">{item.quantity}</div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Full-Screen Lightbox Modal for Presentation Preview */}
      {previewModalIndex !== null && (
        <div
          onClick={() => setPreviewModalIndex(null)}
          className="fixed inset-0 z-[200] bg-black/90 backdrop-blur-md flex items-center justify-center p-4 animate-in fade-in duration-200 no-print select-none"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="relative max-w-5xl w-full max-h-[90vh] flex flex-col items-center justify-center"
          >
            <div className="w-full flex items-center justify-between text-white pb-3 border-b border-white/10 mb-3 px-2">
              <div className="flex items-center space-x-2">
                <ImageIcon className="w-4 h-4 text-brand-400" />
                <span className="font-bold text-xs tracking-wider">
                  Proje Tasarım Detayı {previewModalIndex + 1} / {projectImages.length}
                </span>
              </div>

              <div className="flex items-center space-x-2">
                <button
                  type="button"
                  onClick={() => {
                    const link = document.createElement('a');
                    link.href = projectImages[previewModalIndex];
                    link.download = `Proje_Tasarim_Detayi_${previewModalIndex + 1}.png`;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                  }}
                  className="p-2 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-colors flex items-center space-x-1 text-xs font-semibold"
                  title="Görseli İndir"
                >
                  <Download className="w-4 h-4" />
                  <span className="hidden sm:inline">İndir</span>
                </button>

                <button
                  type="button"
                  onClick={() => setPreviewModalIndex(null)}
                  className="p-2 bg-white/10 hover:bg-red-600/80 text-white rounded-lg transition-colors"
                  title="Kapat (ESC)"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="relative flex items-center justify-center w-full max-h-[75vh] overflow-hidden">
              <img
                src={projectImages[previewModalIndex]}
                alt={`Büyük Proje Görseli ${previewModalIndex + 1}`}
                className="max-h-[75vh] max-w-full object-contain rounded-xl shadow-2xl border border-stone-800 animate-in zoom-in-95 duration-200"
              />

              {projectImages.length > 1 && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setPreviewModalIndex(previewModalIndex > 0 ? previewModalIndex - 1 : projectImages.length - 1);
                  }}
                  className="absolute left-2 p-3 bg-black/60 hover:bg-black/90 text-white rounded-full transition-all border border-white/20 hover:scale-110"
                  title="Önceki Görsel"
                >
                  <ChevronLeft className="w-6 h-6" />
                </button>
              )}

              {projectImages.length > 1 && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setPreviewModalIndex(previewModalIndex < projectImages.length - 1 ? previewModalIndex + 1 : 0);
                  }}
                  className="absolute right-2 p-3 bg-black/60 hover:bg-black/90 text-white rounded-full transition-all border border-white/20 hover:scale-110"
                  title="Sonraki Görsel"
                >
                  <ChevronRight className="w-6 h-6" />
                </button>
              )}
            </div>

            <div className="mt-4 flex items-center justify-center space-x-2">
              {projectImages.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setPreviewModalIndex(i)}
                  className={`w-2.5 h-2.5 rounded-full transition-all ${
                    i === previewModalIndex ? 'bg-brand-500 w-6' : 'bg-white/30 hover:bg-white/60'
                  }`}
                  title={`Görsel ${i + 1}`}
                />
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function ProposalPreview() {
  const {
    mode,
    client,
    config,
    projectImages,
    resolvedModelName,
    resolvedMaterialName,
    resolvedDoorColor,
    resolvedBodyColor,
    resolvedGlassType,
    resolvedProfileColor,
    allVisualAttachmentItems,
    pricingCalculations,
    previewRows,
    hasExtraDiscount,
    cabinetsCampaignSavings,
    otherRowsDiscountSavings,
    selectedStore,
    proposalType,
    projectMeta,
    units,
    salesPointDiscount,
  } = useAppContext();

  const isBulk = proposalType === 'bulk';
  const totalUnitCount = units.reduce((sum, u) => sum + u.quantity, 0);
  const projectGrandTotal = units.reduce((sum, u) => sum + ((u.manualUnitPrice || 0) + (u.manualVat || 0)) * u.quantity, 0);

  // Ticari Koşullar maddelerinde "İntema" kelimesini, seçili satış noktasının Ticari Ünvanı ile değiştirir.
  const ticariUnvan = selectedStore?.ticariUnvan || 'İntema';
  const withTicariUnvan = (text: string) => text.split('İntema').join(ticariUnvan);

  return (
    <div className={`${mode !== 'preview' ? 'hidden' : ''} proposal-preview-figtree bg-white shadow-xl rounded-xl border border-stone-200 max-w-4xl mx-auto p-10 print:border-none print:shadow-none print-container space-y-8`}>

      {/* Repeats at the same top-right spot on every printed page (native browser print behavior for fixed elements) — the per-page inline logos below are hidden in print to avoid duplicates. */}
      {BRAND_LOGO_URL && (
        <img
          src={BRAND_LOGO_URL}
          alt="İntema Logo"
          width={110}
          height={44}
          className="hidden print:block print:fixed print:top-6 print:right-10 print:h-10 print:w-auto object-contain z-50"
        />
      )}

      {/* Cover Page */}
      <div className="page-break-after flex flex-col justify-between min-h-[25cm] print:min-h-0 print:h-[27.7cm] p-8 md:p-16 print:p-10 bg-white border border-stone-150 print:border-none rounded-2xl relative mb-12 print:mb-0 text-left overflow-hidden">
        {/* Masks the global fixed top-right logo (above) on the cover only — the cover already has its own large logo, so the small repeating one would be a redundant duplicate here. A plain in-flow element, not fixed, so it only paints on this page. */}
        <div className="hidden print:block absolute top-0 right-0 w-64 h-16 bg-white z-[60]" />
        {/* Background accents for premium branding in screen view */}
        <div className="absolute top-0 right-0 w-64 h-64 bg-stone-50 rounded-full filter blur-3xl opacity-60 pointer-events-none print:hidden" />
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-brand-50/30 rounded-full filter blur-3xl opacity-50 pointer-events-none print:hidden" />

        {/* Top Section: Logo (Left) and Quick Info (Right) */}
        <div className="flex justify-between items-start pt-4 w-full relative z-10">
          {/* Logo Section (Asymmetric: Aligned Left, Prominent Size as in Reference Image) */}
          <div>
            {BRAND_LOGO_URL ? (
              <img 
                src={BRAND_LOGO_URL} 
                alt="İntema Logo" 
                className="h-16 md:h-20 print:h-[2.2cm] w-auto object-contain" 
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.style.display = 'none';
                  if (target.parentElement) {
                    target.parentElement.innerHTML = '<div class="text-3xl font-bold tracking-widest text-stone-900 font-sans">İNTEMA</div>';
                  }
                }}
              />
            ) : (
              <div className="text-3xl font-bold tracking-widest text-stone-900 font-sans">
                İNTEMA
              </div>
            )}
          </div>

          {/* Quick Info (Right) */}
          {/* print:mt-8 clears the cover-only white mask (top-0 right-0 w-64 h-16) below, which otherwise
              clips the top of "CRM NO" — the mask's 64px height exceeds this block's default ~56px offset. */}
          <div className="text-right text-[9px] text-stone-500 space-y-1 font-sans uppercase tracking-wider print:mt-8">
            <div><strong>CRM NO:</strong> {client.crmNo || '-'}</div>
            <div><strong>TEKLİF TARİHİ:</strong> {formatDateTurkish(client.date)}</div>
            <div><strong>GEÇERLİLİK TARİHİ:</strong> {formatDateTurkish(client.validityDate)}</div>
          </div>
        </div>

        {/* Kitchen Sketch Image */}
        <div className="my-auto py-4 flex items-center justify-center w-full relative z-10">
          <img
            src="/kitchen_sketch.png"
            alt="Mutfak Proje Çizimi"
            className="h-[8cm] md:h-[10cm] print:h-[8.5cm] w-full object-contain"
          />
        </div>

        {/* Title Section (Asymmetric: Bottom Third, Aligned Left) */}
        <div className="text-left mt-auto pt-8 pb-6 space-y-4 max-w-2xl">
          <div className="flex items-center gap-4 text-[9px] text-stone-500 tracking-[0.25em] uppercase font-semibold">
            <div className="w-10 h-[1px] bg-stone-400" />
            <span>KİŞİYE ÖZEL TASARIM & DONANIM</span>
          </div>
          <h1 className="text-2xl md:text-3xl font-light tracking-wider text-stone-800 font-sans uppercase leading-none">
            {isBulk ? (
              <span className="font-bold text-[#945f46]">{projectMeta.projectName || 'TOPLU PROJE'}</span>
            ) : resolvedModelName ? (
              <>
                <span className="font-bold text-[#945f46]">{resolvedModelName}</span> <span className="font-light">MUTFAK PROJESİ</span>
              </>
            ) : (
              <span className="font-bold">MUTFAK PROJESİ</span>
            )}
          </h1>
          <p className="text-[10px] text-stone-400 tracking-wider uppercase font-semibold">
            {isBulk ? `${projectMeta.siteName ? projectMeta.siteName + ' — ' : ''}Toplu Fiyat Teklifi & Teknik Şartname` : 'Fiyat Teklifi & Teknik Şartname'}
          </p>
          {isBulk && (
            <div className="flex items-center gap-6 pt-2 text-[9px] text-stone-500 uppercase tracking-wider font-semibold">
              <span><strong className="text-stone-900">{units.length}</strong> Daire Tipi</span>
              <span><strong className="text-stone-900">{totalUnitCount}</strong> Toplam Daire</span>
            </div>
          )}
        </div>

        {/* Technical Title Block (Antet) */}
        <div className="border-t border-stone-300 pt-6 text-[9.5px] text-stone-600 uppercase font-sans tracking-wider w-full">
          
          {/* 1. DESKTOP VIEW (Perfect horizontal alignment) */}
          <div className="hidden md:block relative space-y-4">
            {/* Continuous Vertical Line */}
            <div className="absolute top-0 bottom-0 left-1/2 w-[1px] bg-stone-200 -ml-[0.5px]" />
            
            {/* Headers */}
            <div className="grid grid-cols-2 gap-12 pb-2">
              <div>
                <span className="text-[8px] font-bold text-[#945f46] tracking-[0.2em]">MÜŞTERİ & PROJE BİLGİLERİ</span>
              </div>
              <div className="pl-8 lg:pl-12">
                <span className="text-[8px] font-bold text-[#945f46] tracking-[0.2em]">SATIŞ NOKTASI & TASARIMCI</span>
              </div>
            </div>
            
            {/* Row 1: Müşteri & Tasarımcı */}
            <div className="grid grid-cols-2 gap-12 items-start">
              <div className="grid grid-cols-[100px_1fr] gap-2 items-baseline">
                <div className="text-[8px] font-semibold text-stone-400 whitespace-nowrap">MÜŞTERİ:</div>
                <div className="font-semibold text-stone-800 truncate" title={client.clientName}>{client.clientName || '-'}</div>
              </div>
              <div className="pl-8 lg:pl-12 grid grid-cols-[100px_1fr] gap-2 items-baseline">
                <div className="text-[8px] font-semibold text-stone-400 whitespace-nowrap">TASARIMCI / MİMAR:</div>
                <div className="font-semibold text-stone-800 truncate" title={client.designer}>{client.designer || '-'}</div>
              </div>
            </div>

            {/* Row 2: Telefon & Telefon */}
            <div className="grid grid-cols-2 gap-12 items-start">
              <div className="grid grid-cols-[100px_1fr] gap-2 items-baseline">
                <div className="text-[8px] font-semibold text-stone-400 whitespace-nowrap">TELEFON:</div>
                <div className="font-semibold text-stone-800">{client.phone || '-'}</div>
              </div>
              <div className="pl-8 lg:pl-12 grid grid-cols-[100px_1fr] gap-2 items-baseline">
                <div className="text-[8px] font-semibold text-stone-400 whitespace-nowrap">TELEFON:</div>
                <div className="font-semibold text-stone-800">{client.designerPhone || (selectedStore && selectedStore.phone) || '-'}</div>
              </div>
            </div>

            {/* Row 3: Teslimat Adresi & Adres */}
            <div className="grid grid-cols-2 gap-12 items-start">
              <div className="grid grid-cols-[100px_1fr] gap-2 items-baseline">
                <div className="text-[8px] font-semibold text-stone-400 whitespace-nowrap">TESLİMAT ADRESİ:</div>
                <div className="font-semibold text-stone-800 normal-case leading-relaxed" title={client.address}>
                  {client.address || '-'}
                </div>
              </div>
              <div className="pl-8 lg:pl-12 grid grid-cols-[100px_1fr] gap-2 items-baseline">
                <div className="text-[8px] font-semibold text-stone-400 whitespace-nowrap">ADRES:</div>
                <div className="font-semibold text-stone-800 normal-case leading-relaxed">
                  {(selectedStore && selectedStore.address) || '-'}
                </div>
              </div>
            </div>

            {/* Row 4: Blank & Satış Noktası */}
            <div className="grid grid-cols-2 gap-12 items-start">
              <div className="grid grid-cols-[100px_1fr] gap-2 items-baseline">
                {/* Empty left side */}
              </div>
              <div className="pl-8 lg:pl-12 grid grid-cols-[100px_1fr] gap-2 items-baseline">
                <div className="text-[8px] font-semibold text-stone-400 whitespace-nowrap">SATIŞ NOKTASI:</div>
                <div className="font-semibold text-stone-800">{client.storeName || '-'}</div>
              </div>
            </div>
          </div>

          {/* 2. MOBILE VIEW (Grouped columns for natural stack) */}
          <div className="md:hidden space-y-6">
            {/* Müşteri & Proje Bilgileri Group */}
            <div className="space-y-3">
              <div className="pb-1.5 mb-2">
                <span className="text-[8px] font-bold text-[#945f46] tracking-[0.2em]">MÜŞTERİ & PROJE BİLGİLERİ</span>
              </div>
              <div className="grid grid-cols-[90px_1fr] gap-2 items-baseline">
                <div className="text-[8px] font-semibold text-stone-400">MÜŞTERİ:</div>
                <div className="font-semibold text-stone-800">{client.clientName || '-'}</div>
              </div>
              <div className="grid grid-cols-[90px_1fr] gap-2 items-baseline">
                <div className="text-[8px] font-semibold text-stone-400">TELEFON:</div>
                <div className="font-semibold text-stone-800">{client.phone || '-'}</div>
              </div>
              <div className="grid grid-cols-[90px_1fr] gap-2 items-baseline">
                <div className="text-[8px] font-semibold text-stone-400">TESLİMAT ADRESİ:</div>
                <div className="font-semibold text-stone-800 normal-case leading-relaxed">{client.address || '-'}</div>
              </div>
            </div>

            {/* Satış Noktası & Tasarımcı Group */}
            <div className="space-y-3 pt-2">
              <div className="pb-1.5 mb-2">
                <span className="text-[8px] font-bold text-[#945f46] tracking-[0.2em]">SATIŞ NOKTASI & TASARIMCI</span>
              </div>
              <div className="grid grid-cols-[90px_1fr] gap-2 items-baseline">
                <div className="text-[8px] font-semibold text-stone-400">TASARIMCI / MİMAR:</div>
                <div className="font-semibold text-stone-800">{client.designer || '-'}</div>
              </div>
              <div className="grid grid-cols-[90px_1fr] gap-2 items-baseline">
                <div className="text-[8px] font-semibold text-stone-400">TELEFON:</div>
                <div className="font-semibold text-stone-800">{client.designerPhone || (selectedStore && selectedStore.phone) || '-'}</div>
              </div>
              <div className="grid grid-cols-[90px_1fr] gap-2 items-baseline">
                <div className="text-[8px] font-semibold text-stone-400">ADRES:</div>
                <div className="font-semibold text-stone-800 normal-case leading-relaxed">{(selectedStore && selectedStore.address) || '-'}</div>
              </div>
              <div className="grid grid-cols-[90px_1fr] gap-2 items-baseline">
                <div className="text-[8px] font-semibold text-stone-400">SATIŞ NOKTASI:</div>
                <div className="font-semibold text-stone-800">{client.storeName || '-'}</div>
              </div>
            </div>
          </div>

        </div>

        {/* Cover Page Footer */}
        <div className="absolute bottom-2 left-8 right-8 md:bottom-2 md:left-16 md:right-16 print:left-10 print:right-10 flex justify-between items-center text-[7pt] text-stone-400 tracking-[0.08em] uppercase font-semibold font-sans">
          <span>© 2026 İntema Mutfak</span>
        </div>
      </div>

      {/* Main Proposal Pages (Specification details, pricing, terms) */}
      <div className="space-y-12">

        {isBulk ? (
          units.map(unit => {
            const names = resolveConfigNames(unit.config);
            const calc = calculatePricing(unit.pricingRows, names.resolvedModelName);
            const preview = buildPreviewPricing(calc);
            return (
              <KitchenPreviewSection
                key={unit.id}
                sectionLabel={`${unit.label || 'İsimsiz Daire Tipi'} (${unit.quantity} adet)`}
                pageNumberPrefix="01"
                config={unit.config}
                resolvedModelName={names.resolvedModelName}
                resolvedMaterialName={names.resolvedMaterialName}
                resolvedDoorColor={names.resolvedDoorColor}
                resolvedBodyColor={names.resolvedBodyColor}
                resolvedGlassType={names.resolvedGlassType}
                resolvedProfileColor={names.resolvedProfileColor}
                pricingCalculations={calc}
                previewRows={preview.previewRows}
                hasExtraDiscount={preview.hasExtraDiscount}
                cabinetsCampaignSavings={preview.cabinetsCampaignSavings}
                otherRowsDiscountSavings={preview.otherRowsDiscountSavings}
                allVisualAttachmentItems={sortVisualAttachmentItems(unit.specItems)}
                projectImages={unit.projectImages}
                quantity={unit.quantity}
                showPricing={false}
              />
            );
          })
        ) : null}

        {isBulk && (
          <div className="page-break-before space-y-6">
            <div className="page-break-before flex items-center space-x-3 pt-6 pb-2">
              <Building2 className="w-4 h-4 text-brand-600 shrink-0" />
              <h2 className="font-semibold text-stone-800 uppercase tracking-[0.15em] text-[10pt]">Toplam Maliyet Dökümü</h2>
              <div className="flex-1 h-[1px] bg-stone-200" />
            </div>
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-stone-300 text-stone-500 uppercase text-[6.5pt] font-semibold tracking-wider">
                  <th className="py-2.5">Daire Tipi</th>
                  <th className="py-2.5 px-4 text-center w-16">Adet</th>
                  <th className="py-2.5 px-4 text-right w-32">Özel İskontolu Birim Fiyat</th>
                  <th className="py-2.5 px-4 text-right w-24">KDV</th>
                  <th className="py-2.5 px-4 text-right w-28">Tutar</th>
                  <th className="py-2.5 pl-4 text-right w-32">Toplam Fiyat</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100 text-[8.5pt]">
                {units.map(unit => {
                  const unitPrice = unit.manualUnitPrice || 0;
                  const unitVat = unit.manualVat || 0;
                  const unitAmount = unitPrice + unitVat;
                  return (
                    <tr key={unit.id} className="page-break-inside-avoid">
                      <td className="py-3 font-normal text-stone-800">{unit.label || 'İsimsiz Daire Tipi'}</td>
                      <td className="py-3 px-4 text-center text-stone-600">{unit.quantity}</td>
                      <td className="py-3 px-4 text-right text-stone-600">{formatTL(unitPrice)}</td>
                      <td className="py-3 px-4 text-right text-stone-600">{formatTL(unitVat)}</td>
                      <td className="py-3 px-4 text-right text-stone-600">{formatTL(unitAmount)}</td>
                      <td className="py-3 pl-4 text-right font-semibold text-stone-800">{formatTL(unitAmount * unit.quantity)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div className="flex justify-between items-center page-break-inside-avoid">
              <span className="text-[8.5pt] font-semibold text-stone-500 uppercase tracking-wider">
                Toplam Daire: {totalUnitCount}
              </span>
              <div className="flex justify-between items-center gap-3 border-t-2 border-stone-800 pt-4 text-stone-900 w-96">
                <span className="font-bold uppercase tracking-wider text-[8pt] shrink-0">Genel Proje Toplamı (KDV Dahil):</span>
                <span className="text-[13pt] font-bold text-stone-900 whitespace-nowrap">{formatTL(projectGrandTotal)}</span>
              </div>
            </div>
          </div>
        )}

        {!isBulk && (
          <KitchenPreviewSection
            pageNumberPrefix="01"
            config={config}
            resolvedModelName={resolvedModelName}
            resolvedMaterialName={resolvedMaterialName}
            resolvedDoorColor={resolvedDoorColor}
            resolvedBodyColor={resolvedBodyColor}
            resolvedGlassType={resolvedGlassType}
            resolvedProfileColor={resolvedProfileColor}
            pricingCalculations={pricingCalculations}
            previewRows={previewRows}
            hasExtraDiscount={hasExtraDiscount}
            cabinetsCampaignSavings={cabinetsCampaignSavings}
            otherRowsDiscountSavings={otherRowsDiscountSavings}
            allVisualAttachmentItems={allVisualAttachmentItems}
            projectImages={projectImages}
            salesPointDiscount={salesPointDiscount}
          />
        )}
        {/* Page: Ticari Koşullar */}
        <div className="page-break-before pt-10 space-y-6">
          <div className="flex justify-between items-center border-b border-stone-200 pb-2">
            <h2 className="font-semibold text-stone-850 uppercase tracking-[0.18em] text-[8pt]">Ticari Koşullar</h2>
            {BRAND_LOGO_URL && (
              <img src={BRAND_LOGO_URL} alt="İntema Logo" width={110} height={44} className="h-10 md:h-11 print:hidden w-auto object-contain shrink-0" />
            )}
          </div>
          
          <ul className="space-y-3.5 text-[11px] text-stone-600 leading-relaxed list-disc pl-5">
            <li>Sipariş girişi esnasında sipariş bedelinin tamamı tahsil edilmektedir. Fatura, ürün teslimatından sonra yedi iş günü içinde fatura alıcısına ulaştırılır. Ürün garanti sürelerinin sonuna kadar faturanın müşteri tarafından saklanması gerekmektedir.</li>
            <li>Ürün teslimatları, Pazar günleri hariç her gün Lojistik birimimiz tarafından 1 gün önce bilgi verilerek 10.00-19.00 saatleri arası yapılmaktadır. Teslimat saatleri çevresel etmenlere bağlı olarak aynı gün içerisinde belirtilen saat aralığı dışında gerçekleşebilir.</li>
            <li>Teslimat tarihi müşteriden kaynaklanan nedenlerden dolayı değiştirilmek istendiğinde, mevcut tarihinden minimum 48 saat önce satış noktasındaki sorumlu mimara bilgi verilmesi gerekmektedir. Bu tarih sevkiyat gününde değiştirilirse veya müşteri kaynaklı nedenlerle yapılamazsa ikinci sevkiyat için sevk bedeli tahsil edilir.</li>
            {!isBulk && (
              <li>{withTicariUnvan("Müşteri siparişleri İntema tarafından sevke hazır ürünlerden en fazla 45 gün ileri tarihli teslim olarak sipariş edilebilir. Sipariş girişlerinde ileri tarihli istenen teslim tarihinin Adeko sisteminde sipariş aşamasında mutlaka girilmesi gereklidir.")}</li>
            )}
            {!isBulk && (
              <li>{withTicariUnvan("İntema tarafından sevke hazır edilmiş bir mutfak, müşteri talebine istinaden en fazla 15 gün bila bedel bekletilebilir. 15 günlük sürenin hitamından evvel müşteri mutfağı teslim almalıdır.")}</li>
            )}
            {!isBulk && (
              <li>{withTicariUnvan("İntema tarafından sevke hazır edilmiş bir mutfağın, müşteri talebine istinaden bekletilebileceği en fazla süre olan 15 gün akabinde, müşteri mutfağı teslim alamıyor ve 15 günlük süre aşılıyorsa, bu durumda sevke hazır ürünün bekletileceği depo da nazara alınarak müşteri ile yed-i emin tutanağı düzenlenir. Yed-i emin tutanağının tanzimi ile birlikte ve her halükarda yukarıda belirtilen 15 günlük bekleme süresinin sonunda sevke hazır mutfağın faturası düzenlenerek müşteriye iletilir ve ilgili depo kirası da müşteriden tahsil edilir. Ürünlerin sorumluluğu müşterinin uhdesinde olmak kaydı ile ürünler en fazla 60 gün yed-i emin deposunda bekletilir.")}</li>
            )}
            <li>{withTicariUnvan("Projede kullanılması planlanan her türlü cihaz, eviye, batarya, mutfak aletleri (ocak, fırın, davlumbaz vb.) gibi mutfak tamamlayıcılarının siparişten önce proje danışmanı ile eksiksiz paylaşılması ve projenin müşteri tarafından verilen bu bilgilere göre tasarlanması gerekir. Eksik bilgi olması durumunda sipariş girişi yapılmaz. Müşterinin talebi ile yapılması durumunda müşteri projede belirlenen mutfak ve tesisat planına uygun olarak cihaz seçimi yapacağını, sipariş akabinde seçeceği cihazların mutfak ve tesisat planına uygun olmaması halinde İntema'yı sorumlu tutmayacağını, inisiyatifin kendisinde olduğunu taahhüt eder. Tamamlayıcı ürünler hakkındaki yeterli bilgi olmamasından kaynaklı hatalarda İntema sorumlu tutulamaz.")}</li>
            {!isBulk && (
              <li>Mutfak tezgahları, dolaplar yerine takıldıktan sonra ilgili firma yetkilisi tarafından alınacak ölçüye göre üretilerek, ortalama 10 iş günü içerisinde teslim edilir. Stoklarda olmayan mutfak tezgahları için teslim tarihi ayrıca tarafınıza bildirilir.</li>
            )}
            <li>{withTicariUnvan("Montaj, bakım ve onarım hizmetlerini, İntema Yetkili Servisleri'nden almanızı öneririz. Yetkili servislerimiz, randevu talebinizin ardından 7 iş günü içerisinde size hizmet vermektedir.")}</li>
            <li>{withTicariUnvan("Mutfak projesine istinaden İntema proje danışmanı tarafından verilecek tesisat projesine uyumlu olarak montaj yapılacak alanın hazırlanmış olması, müşteri sorumluluğundadır. Aksi durumda montaj ertelenecektir.")}</li>
            <li>{withTicariUnvan("Müşteri, siparişe ilişkin herhangi bir değişiklik veya iptal talebini, sipariş onay bildiriminin kendisine iletilmesinden itibaren 24 (yirmi dört) saat içinde yazılı olarak İntema'ya bildirmekle yükümlüdür. Söz konusu süre içinde iptal talebinde bulunulmayan siparişler kesinleşmiş kabul edilir; bu tarihten sonra yapılan iptal talepleri değerlendirmeye alınmaz.")}</li>
            <li>{withTicariUnvan("Müşteri tarafından veya müşterinin talebi doğrultusunda üçüncü kişilerce gerçekleştirilen her türlü yapısal, mekanik veya estetik değişiklik; orijinal bileşenlerin yetkisiz parçalarla ikame edilmesi; ürünün teknik şartnamesine aykırı biçimde kullanılması ya da orijinal tasarımının dışına çıkılması halinde, ilgili ürün Şirket'in sunduğu ticari garanti kapsamı dışında kalır. Bu nitelikteki müdahalelerden kaynaklanan arıza, hasar veya kayıplara ilişkin olarak İntema herhangi bir sorumluluk üstlenmez. Yasal garanti hakları saklıdır.")}</li>
            <li>İşbu teklif kapsamında yer almayan tüm detaylar müşteriye sözlü olarak izah edilmiş ve onayı alınmıştır.</li>
            <li>Mutfak görselleri temsili 3 boyutlu simülasyonlardır. Renk ve dokular ışık koşullarına bağlı olarak farklılık gösterebilir. Doğru örnek için yetkili satış noktalarındaki numuneler esas alınmalıdır.</li>
          </ul>
        </div>

        {/* Bulk-only continuation of Ticari Koşullar, forced onto its own fresh page rather than left to auto-overflow the base list above — a manual page-break-before guarantees this block always starts with its own top padding (so it never collides with the repeating fixed logo), instead of relying on the browser to insert a mid-list break with no header space. */}
        {isBulk && (
          // No repeated title/inline logo here by design — the page-break-before start gives this
          // block its own top padding, which is enough for it to clear the global fixed-position
          // logo (defined above) that already repeats on every printed page.
          <div className="page-break-before pt-10 space-y-6">
            <ul className="space-y-3.5 text-[11px] text-stone-600 leading-relaxed list-disc pl-5">
                <li>Fiyatlarımıza KDV dahildir.</li>
                <li>
                  Ödeme Şekli:{' '}
                  {projectMeta.paymentTerms ? (
                    projectMeta.paymentTerms
                  ) : (
                    <span className="text-red-600 font-bold text-[8.5px] uppercase tracking-wide whitespace-nowrap">(Doldurulması zorunlu alanlardır)</span>
                  )}
                </li>
                <li>Ürünler montedir, fiyatlarımıza nakliye ve montaj dahildir.</li>
                <li>Raylar Hettich, evye örtüsü, çöp kovası ve kaşıklıklar yerli marka olacaktır.</li>
                <li>Projede herhangi bir değişiklik olması durumunda tekrar teklif istenmelidir.</li>
                <li>Ürünler aşağıdaki sevk planına göre üretilecek olup, üretim süreci tamamlandığında montaj yeri kaynaklı gecikmelerde, ürünlerin depolama ve koruma sorumluluğu iş verene aittir.</li>
                <li>
                  Ürünlere ait siparişler için son sipariş tarihi{' '}
                  {projectMeta.orderDeadline ? (
                    <span className="font-semibold text-stone-800 whitespace-nowrap">{formatDateNumeric(projectMeta.orderDeadline)} </span>
                  ) : (
                    <>
                      <span className="inline-block border-b border-stone-400 px-1.5 font-semibold text-stone-800 whitespace-nowrap">GG.AA.YYYY</span>{' '}
                      <span className="text-red-600 font-bold text-[8.5px] uppercase tracking-wide whitespace-nowrap">(Doldurulması zorunlu alanlardır)</span>{' '}
                    </>
                  )}
                  tarihidir. Siparişlerin bu tarihten sonra girilmesi durumda oluşacak fiyat farkları iş verene aittir.
                </li>
                <li>
                  Ürün teslimleri{' '}
                  {projectMeta.deliveryMonth ? (
                    <span className="font-semibold text-stone-800">{formatMonthYearTurkish(projectMeta.deliveryMonth)}</span>
                  ) : (
                    <span className="inline-block border-b border-stone-400 px-2 text-stone-400 italic font-normal">Ocak 2025</span>
                  )}
                  .
                </li>
                <li>
                  {projectMeta.finalDeliveryDeadline ? (
                    <span className="font-semibold text-stone-800 whitespace-nowrap">{formatMonthYearTurkish(projectMeta.finalDeliveryDeadline)} </span>
                  ) : (
                    <>
                      <span className="inline-block border-b border-stone-400 px-2 text-stone-400 italic font-normal">Ocak 2025</span>{' '}
                      <span className="text-red-600 font-bold text-[8.5px] uppercase tracking-wide whitespace-nowrap">(Doldurulması zorunlu alanlardır)</span>{' '}
                    </>
                  )}
                  ayı sonuna kadar ürünlerin teslim alınmaması durumunda metreküp başı günlük 15 TL + KDV depolama bedeli fatura edilecektir.
                </li>
                <li>{withTicariUnvan("Şantiyede yatay taşıma için forklift/bobcat, manito vb.; dikey taşıma için asansör ve/veya animak vb. ihtiyaçların karşılanması iş veren sorumluluğundadır. İş veren bunu karşılayamaz ise operasyon İntema tarafından yapılacak olup, ilgili iş verene aittir.")}</li>
                <li>Ürünler paletsiz olarak sevk edilecektir. Depolama nedeni ile paletli olarak sevk edilmesi halinde oluşacak ilave bedeller iş veren sorumluluğundadır.</li>
                <li>Şantiye sahasında iş veren tarafından formen talep edilmesi durumunda, formen sağlanması sorumluluğu iş verene aittir.</li>
                <li>Proje kapsamında parçalı sevk ve/veya montaj istenmesi durumunda oluşacak ilave giderler iş veren sorumluluğundadır.</li>
                <li>Fiyatın sabitlenebilmesi için ödemenin tamamlanması gerekmektedir. Fiyat sabitlendikten sonra malzeme ve aksesuar değişikliği olmaması gerekmektedir. Pozitif yöndeki metraj ve ürün değişiklikleri güncel fiyatlar ile hesaplanacaktır.</li>
                <li>İş programının yıllara sair olması durumunda oluşacak ilave nakliye ve montaj bedelleri iş veren sorumluluğundadır.</li>
                <li>Numune daire talep edilmesi durumunda sadece numune daire sevkinden dolayı ilave nakliye taşıma bedeli iş veren sorumluluğundadır.</li>
            </ul>
          </div>
        )}

        {/* Page: Depolama Koşulları */}
        <div className="page-break-before pt-6 flex flex-col justify-between min-h-[25cm] print:min-h-[27cm]">
          <div className="space-y-4">
            <div className="pb-4 border-b border-stone-200 text-center text-xs text-stone-500 font-medium">
              Şikayet ve önerileriniz için ilgili proje yetkilisini veya 0850 311 70 70 numaralı hattımızı arayarak bilgi alabilirsiniz.
            </div>

            <div className="flex justify-between items-center border-b border-stone-200 pb-2">
              <h2 className="font-semibold text-stone-850 uppercase tracking-[0.18em] text-[8pt]">Depolama Koşulları</h2>
              {BRAND_LOGO_URL && (
                <img src={BRAND_LOGO_URL} alt="İntema Logo" width={110} height={44} className="h-10 md:h-11 print:hidden w-auto object-contain shrink-0" />
              )}
            </div>

            <div className="text-[10px] text-stone-600 space-y-3 pb-2">
              <p className="font-semibold text-stone-800 uppercase text-[9.5px] tracking-wider">
                DEPOLAMA KOŞULLARINA İLİŞKİN VE MONTAJ SONRASI DİKKAT EDİLMESİ GEREKEN EK HUSUSLAR
              </p>

              <div className="grid grid-cols-1 md:grid-cols-3 print:grid-cols-3 gap-4 text-[9.5px] text-stone-650 bg-stone-50 p-3 rounded border border-stone-150 leading-relaxed">
                <p>• Doğal kaplama ürünlerde desen ve renk farklılıkları, ahşabın doğası gereği çeşitli sebeplerle ortaya çıkabilir. Bu sebepler ağacın menşei, yetiştiği iklim koşulları, boyutu, gözenek yapısı, lif yapısı, kurululuk ve sertlik oranı vb'dir.</p>
                <p>• Depolama süresi, depolama koşulları ve montaj sonrası ürünün maruz kaldığı ısı, mevsim değişiklikleri, nem ve ışık gibi faktörler, ürünün renk değişikliğine neden olabilir. Dolayısıyla sonradan verilecek ek siparişleri ile ana mutfak siparişi arasında renk, ton, desen farklılıkları olabilir.</p>
                <p>• Yukarıdaki bilgiler sipariş onayı öncesinde müşteri ile detaylıca yazılı ve sözlü olarak paylaşılmış olup, malzemenin doğası gereği oluşabilecek renk ve desen farklılıkları müşteri tarafından peşinen kabul edilmiş sayılır.</p>
              </div>

              <p className="text-[9.5px] text-stone-500 leading-relaxed mt-4">
                Ürününüzden en yüksek verimi alabilmek ve uzun ömürlü kullanım sağlayabilmek için satın aldığınız ürünü; teslim sırasında tarafınıza iletilen kullanım kılavuzunda yer alan talimatlara uygun biçimde depolamanızı, temizlemenizi ve kullanmanızı tavsiye ederiz. Kılavuzda belirtilen öneriler, ürününüzün performansını ve görünümünü uzun süre koruyabilmesi amacıyla özenle hazırlanmıştır.
              </p>
            </div>
          </div>

          {/* Acceptance / Confirmation Block */}
          <div className="mt-auto pt-6 border-t border-stone-200 page-break-inside-avoid">
            <p className="text-[8.5pt] text-stone-600 font-medium mb-6 italic text-center">
              İşbu fiyat teklifini, teknik detayları, ekli görsel ürün listesini, ticari ve depolama koşullarını okuduğumu, anladığımı ve onayladığımı beyan ederim.
            </p>
            
            <div className="grid grid-cols-2 gap-12 text-[9pt] mt-4">
              {/* Customer Section */}
              <div className="space-y-4">
                <div className="text-[7.5pt] font-semibold text-stone-400 uppercase tracking-wider">TEKLİFİ ONAYLAYAN (MÜŞTERİ)</div>
                <div className="space-y-2 text-stone-800">
                  <div><strong>AD SOYAD:</strong> {client.clientName || '___________________________'}</div>
                  <div><strong>TARİH:</strong> ______ / ______ / ________</div>
                  <div className="pt-8">
                    <strong>İMZA:</strong>
                    <div className="mt-2 border-b border-stone-300 border-dashed w-48 h-10" />
                  </div>
                </div>
              </div>

              {/* Sales Rep / Designer Section */}
              <div className="space-y-4 border-l border-stone-200 pl-8">
                <div className="text-[7.5pt] font-semibold text-stone-400 uppercase tracking-wider">TEKLİFİ HAZIRLAYAN (TASARIMCI / MİMAR)</div>
                <div className="space-y-2 text-stone-800">
                  <div><strong>AD SOYAD:</strong> {client.designer || '___________________________'}</div>
                  <div><strong>TARİH:</strong> ______ / ______ / ________</div>
                  <div className="pt-8">
                    <strong>İMZA:</strong>
                    <div className="mt-2 border-b border-stone-300 border-dashed w-48 h-10" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>

    </div>
  );
}
